const { useCallback, useContext, useEffect, useRef, useState } = React;
import { copyAssetImage, dbCreatePhotoEntry, dbDeletePhotoEntry, dbListPhotoLibrary, dbSubscribePhotoLibrary, dbUpdatePhotoEntry, deleteCueImage, normalizePhotoName } from "./data.jsx";

const PhotoLibraryContext = React.createContext({
  list: [], loading: true, refresh: async () => {},
  lockToLibrary: async () => { throw new Error("no-provider"); },
  addFreshToLibrary: async () => { throw new Error("no-provider"); },
  applyFromLibrary: async () => { throw new Error("no-provider"); },
  removeFromLibrary: async () => false,
  renamePhoto: async () => { throw new Error("no-provider"); },
  replacePhoto: async () => { throw new Error("no-provider"); },
});
function usePhotoLibrary() { return useContext(PhotoLibraryContext); }

// add a just-created entry to the list — unless the live subscription has
// already delivered it (it can arrive before the create call returns)
const withEntry = (list, entry) => (list.some((p) => p.id === entry.id) ? list : [...list, entry].sort((a, b) => a.name.localeCompare(b.name)));

function PhotoLibraryProvider({ children }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const listRef = useRef(list);
  listRef.current = list;

  const refresh = useCallback(async () => {
    setLoading(true);
    const items = await dbListPhotoLibrary();
    setList(items);
    setLoading(false);
  }, []);
  // real-time subscription instead of a one-time fetch on mount — a photo
  // added/renamed/removed by anyone (including data written straight into
  // the db, outside the app) shows up here without a page reload
  useEffect(() => {
    setLoading(true);
    const unsub = dbSubscribePhotoLibrary(
      (items) => { setList(items); setLoading(false); },
      () => { setLoading(false); }
    );
    return unsub;
  }, []);

  const checkNameFree = (name, excludeId) => {
    const trimmed = String(name || "").trim();
    if (!trimmed) throw new Error("empty-name");
    const key = normalizePhotoName(trimmed);
    if (listRef.current.some((p) => p.id !== excludeId && normalizePhotoName(p.name) === key)) throw new Error("duplicate-name");
    return trimmed;
  };

  // lock a photo that's already in use on a cue/block into the library
  const lockToLibrary = useCallback(async (name, sourceAssetId) => {
    const trimmed = checkNameFree(name);
    const copiedId = await copyAssetImage(sourceAssetId);
    const id = await dbCreatePhotoEntry({ name: trimmed, bg: copiedId });
    const entry = { id, name: trimmed, bg: copiedId, createdAt: Date.now() };
    setList((prev) => withEntry(prev, entry));
    return entry;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // add a photo straight from a fresh upload (the Photo Library screen's own
  // uploader) — the asset is already private, so no copy is needed
  const addFreshToLibrary = useCallback(async (name, freshAssetId) => {
    const trimmed = checkNameFree(name);
    const id = await dbCreatePhotoEntry({ name: trimmed, bg: freshAssetId });
    const entry = { id, name: trimmed, bg: freshAssetId, createdAt: Date.now() };
    setList((prev) => withEntry(prev, entry));
    return entry;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // pull a library photo onto a cue/block — always a fresh private copy
  const applyFromLibrary = useCallback(async (entry) => copyAssetImage(entry.bg), []);

  const removeFromLibrary = useCallback(async (id) => {
    const entry = listRef.current.find((p) => p.id === id);
    const ok = await dbDeletePhotoEntry(id);
    if (ok) {
      setList((prev) => prev.filter((p) => p.id !== id));
      if (entry) deleteCueImage(entry.bg);
    }
    return ok;
  }, []);

  // rename an existing library entry, one at a time — same uniqueness rule
  // as adding a new one, but a name isn't a duplicate of itself
  const renamePhoto = useCallback(async (id, newName) => {
    const trimmed = checkNameFree(newName, id);
    const ok = await dbUpdatePhotoEntry(id, { name: trimmed });
    if (ok) setList((prev) => prev.map((p) => (p.id === id ? { ...p, name: trimmed } : p)).sort((a, b) => a.name.localeCompare(b.name)));
    else throw new Error("rename-failed");
    return trimmed;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // swap an entry's picture for a freshly uploaded one, keeping its name.
  // Cues that already used it hold their own private copy and keep the old
  // picture; anything that looks it up by name (pool items) shows the new one.
  const replacePhoto = useCallback(async (id, freshAssetId) => {
    const entry = listRef.current.find((p) => p.id === id);
    const ok = await dbUpdatePhotoEntry(id, { bg: freshAssetId });
    if (!ok) { deleteCueImage(freshAssetId); throw new Error("replace-failed"); }
    setList((prev) => prev.map((p) => (p.id === id ? { ...p, bg: freshAssetId } : p)));
    if (entry && entry.bg && entry.bg !== freshAssetId) deleteCueImage(entry.bg);
  }, []);

  return (
    <PhotoLibraryContext.Provider value={{ list, loading, refresh, lockToLibrary, addFreshToLibrary, applyFromLibrary, removeFromLibrary, renamePhoto, replacePhoto }}>
      {children}
    </PhotoLibraryContext.Provider>
  );
}

export { PhotoLibraryContext, usePhotoLibrary, PhotoLibraryProvider };
